import { CustomCellEditorProps } from '@mint-lib/ag-grid/react';

import {
  ColDef,
  type ColumnEventType,
  GetContextMenuItemsParams,
  GridApi,
  ValueGetterParams,
  ValueSetterParams,
} from '@mint-lib/ag-grid/community';
import type { GoalCellValue } from '@mint-lib/ag-grid/editors';
import type { ITooltipParams } from '@mint-lib/ag-grid/enterprise';
import {
  colDefWithErrorHandling,
  createContext,
} from '@mint-lib/ag-grid/factory';
import type { MediaPlan } from '@mint-sdk/api-media-plan';
import { compareAsc, parseISO } from 'date-fns';

import {
  CELL_EDITOR_BY_TYPE,
  CELL_RENDERER_BY_TYPE,
  EMPTY_CELL_VALUE,
  POPUP_EDITORS_TYPES,
  TOTALS_ROW_ID,
} from '../consts/grid.consts.jsx';
import type { GoalOptionGroupsType } from '../hooks/useGoalOptionGroups.js';
import PinnedRightButton from '../pages/components/PlanningContent/LevelDetails/TableGrid/PinnedRightButton/PinnedRightButton.jsx';
import {
  CellEditorParamsMapType,
  CellHandlersType,
} from '../types/grid.types.js';

const getPopupSettings = (column: MediaPlan.Column) => {
  if (POPUP_EDITORS_TYPES.includes(column.type)) {
    return {
      cellEditorPopup: true,
      cellEditorPopupPosition: 'under',
    } as const;
  }
  return {};
};

const pinnedRightColumnConfig: ColDef = {
  pinned: 'right',
  cellRenderer: PinnedRightButton,
  tooltipValueGetter: (params) => {
    const { t, isAddRowDisabled } = params.context;

    if (
      !params.data ||
      params.data.levelType === 'media_row' ||
      params.data.id === TOTALS_ROW_ID ||
      isAddRowDisabled
    ) {
      return;
    }

    return t('Add item to the media structure');
  },
  maxWidth: 40,
  minWidth: 40,
  resizable: false,
  suppressMovable: true,
  suppressColumnsToolPanel: true,
  suppressHeaderMenuButton: true,
  lockPosition: 'right',
  cellStyle: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 0,
  },
};

const SHOW_TOOLTIP_THRESHOLD = 20;

const textTooltipValueGetter = (
  params: ITooltipParams<MediaPlan.MediaData>,
  column: MediaPlan.Column,
) => {
  const value = params.data?.attributes[column.id]?.value;

  if (typeof value === 'string' && value.length > SHOW_TOOLTIP_THRESHOLD) {
    return value;
  }
};

const selectTooltipValueGetter = (
  params: ITooltipParams<MediaPlan.MediaData>,
  column: MediaPlan.Column,
) => {
  const value = params.data?.attributes[column.id]?.value;
  const label = column.options?.find((option) => option.value === value)?.label;

  if (typeof label === 'string' && label.length > SHOW_TOOLTIP_THRESHOLD) {
    return label;
  }
};

type AttributeValue = MediaPlan.MediaData['attributes'][string]['value'];
type GoalAttributeValue = Extract<AttributeValue, { refUrn: string }>;

const isGoalAttributeValue = (
  value: AttributeValue | undefined,
): value is GoalAttributeValue =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  'refUrn' in value;

const goalValueGetter = (
  params: ValueGetterParams<MediaPlan.MediaData>,
  column: MediaPlan.Column,
): GoalCellValue | null => {
  const value = params.data?.attributes[column.id]?.value;

  return isGoalAttributeValue(value)
    ? { id: value.refUrn, value: value.value }
    : null;
};

const goalValueSetter = (
  params: ValueSetterParams<MediaPlan.MediaData, GoalCellValue>,
  column: MediaPlan.Column,
): boolean => {
  const attribute = params.data.attributes[column.id];

  if (!attribute) {
    return false;
  }

  attribute.value = params.newValue
    ? { refUrn: params.newValue.id, value: params.newValue.value }
    : EMPTY_CELL_VALUE;

  return true;
};

type ColumnValueConfig = {
  valueGetter: (
    params: ValueGetterParams<MediaPlan.MediaData>,
  ) => AttributeValue | null | undefined;
  valueSetter?: (params: ValueSetterParams<MediaPlan.MediaData>) => boolean;
};

const getColumnValueConfig = (column: MediaPlan.Column): ColumnValueConfig => {
  if (column.type === 'reference') {
    return {
      valueGetter: (params) =>
        goalValueGetter(params, column) as unknown as AttributeValue,
      valueSetter: (params) => goalValueSetter(params, column),
    };
  }

  return {
    valueGetter: (params) => params.data?.attributes[column.id]?.value,
  };
};

export const prepareColumns = (
  columns: MediaPlan.Column[],
  cellEditorParamsMap: Partial<CellEditorParamsMapType>,
  cellHandlers: CellHandlersType,
  isReadOnlyPermission: boolean,
  goalGroups: GoalOptionGroupsType,
): ColDef<MediaPlan.MediaData>[] => {
  const goalOptionsById = new Map(
    goalGroups.flatMap((group) =>
      group.options.map((option) => [option.value, option]),
    ),
  );

  const preparedColumns = columns.map((column): ColDef<MediaPlan.MediaData> => {
    const isRelatedSelect =
      column.type === 'select' && column.primaryFieldsUrns.length > 0;
    const cellType = isRelatedSelect ? 'related_select' : column.type;

    return colDefWithErrorHandling<MediaPlan.MediaData, AttributeValue>({
      colId: column.id,
      headerName: column.label,
      headerTooltip: column.label,
      ...getColumnValueConfig(column),
      tooltipValueGetter: (params) => {
        if (column.type === 'free_form_text') {
          return textTooltipValueGetter(params, column);
        }

        if (column.type === 'select') {
          return selectTooltipValueGetter(params, column);
        }
      },
      field: `attributes.${column.id}.value`,
      cellRenderer: CELL_RENDERER_BY_TYPE[column.type],
      cellEditor: CELL_EDITOR_BY_TYPE[cellType],
      onCellValueChanged: cellHandlers[column.type] ?? cellHandlers.basic,
      lockVisible: column.isMandatory,
      hide: column.isHidden,
      cellEditorParams: (params: CustomCellEditorProps) => {
        return cellEditorParamsMap[cellType]?.(params);
      },
      editable: (params) => {
        return (
          !params.data?.attributes[column.id].isReadOnly &&
          !isReadOnlyPermission
        );
      },
      cellRendererParams: {
        options: column.options,
        disabledOptions: column.disabledOptions,
        decimalPlaces: column.decimalPlaces,
        goalOptionsById,
      },
      ...getPopupSettings(column),
    });
  });

  return [...preparedColumns, pinnedRightColumnConfig];
};

export function getRouteByRowId(
  gridRowId: string,
  gridApi: GridApi<MediaPlan.MediaData> | null,
) {
  return gridApi?.getRowNode(gridRowId)?.parent?.getRoute();
}

/**
 * Get the column name from the column value access string.
 * @param accessValueString The access value string, e.g., "attributes.columnName.value".
 * @returns The column name, e.g., "columnName".
 */
export function getColName(accessValueString: string) {
  return accessValueString.split('.')[1];
}

const USER_COLUMN_CHANGE_SOURCES: ReadonlySet<string> =
  new Set<ColumnEventType>([
    'toolPanelUi',
    'toolPanelDragAndDrop',
    'uiColumnMoved',
    'uiColumnDragged',
    'columnMenu',
    'contextMenu',
  ]);

export const isUserColumnChange = (source: string): boolean =>
  USER_COLUMN_CHANGE_SOURCES.has(source);

export const getIsClearValueDisabled = (
  params: GetContextMenuItemsParams<MediaPlan.MediaData>,
) => {
  if (
    !params.node ||
    !params.column ||
    params.column.getColDef().field === 'name'
  ) {
    return true;
  }

  const editable = params.column.isCellEditable(params.node);

  return !editable || !params.value;
};

export type ErrorData = {
  message: string;
  errorValue: string | string[] | number | null;
};

type GridContext = ReturnType<typeof createContext>;

type UpdateErrorInContextParams<T> = {
  rowId: string;
  colId: string;
  errorData: ErrorData | null;
  gridApi: GridApi<T> | null;
};

export function updateErrorInContext<T>({
  rowId,
  colId,
  errorData,
  gridApi,
}: UpdateErrorInContextParams<T>) {
  if (!gridApi) return;

  const context: GridContext = gridApi.getGridOption('context');

  const nextContext = {
    ...context,
    errors: {
      ...context.errors,
      [rowId]: {
        ...context.errors[rowId],
      },
    },
  };

  if (errorData) {
    nextContext.errors[rowId][colId] = errorData;
  } else {
    delete nextContext.errors[rowId][colId];
  }

  gridApi.setGridOption('context', nextContext);
}

/**
 * The function calculates the date constraints based on the current date value and the min/max date from the api response
 * @param params the current cell params
 * @param restrictions the restrictions from the api response
 * @returns the date constraints
 */
export const calculateDateConstraints = (
  params: CustomCellEditorProps<MediaPlan.MediaData>,
  restrictions?: MediaPlan.RestrictionsResponse,
) => {
  if (!restrictions?.maxDate || !restrictions?.minDate) {
    return {};
  }

  const attributes = params.data.attributes;
  const isStartDateCol = params?.colDef?.colId?.includes('date_from');
  const isEndDateCol = params?.colDef?.colId?.includes('date_to');

  const dateToAttrKey = Object.keys(attributes).find((attr) =>
    attr.includes('date_to'),
  );
  const dateFromAttrKey = Object.keys(attributes).find((attr) =>
    attr.includes('date_from'),
  );

  const maxDate = parseISO(restrictions.maxDate);
  const minDate = parseISO(restrictions.minDate);

  if (!dateToAttrKey || !dateFromAttrKey) {
    return {};
  }

  const otherDate = isStartDateCol
    ? attributes[dateToAttrKey].value
    : attributes[dateFromAttrKey].value;

  if (!otherDate || typeof otherDate !== 'string') {
    return {
      disabledDates: {
        before: minDate,
        after: maxDate,
      },
    };
  }

  const parsedOtherDate = parseISO(otherDate);

  const getOtherDateConstrained = () => {
    if (isStartDateCol) {
      return compareAsc(parsedOtherDate, maxDate) === -1
        ? parsedOtherDate
        : maxDate;
    } else {
      return compareAsc(parsedOtherDate, minDate) === 1
        ? parsedOtherDate
        : minDate;
    }
  };

  const otherDateConstrained = getOtherDateConstrained();

  return {
    disabledDates: {
      before: isStartDateCol ? minDate : otherDateConstrained,
      after: isEndDateCol ? maxDate : otherDateConstrained,
    },
  };
};
